/**
 * Territory display + selection-normalisation helpers — the single source of truth for
 * turning stored territory codes into human labels and for collapsing a selection into
 * region labels.
 *
 * Pure (no React) on purpose: BOTH the `<TerritorySelector>` picker AND non-component code
 * (lib formatters, pages, the deal console) share this one implementation, so every surface
 * renders territories identically. Region selections are stored as their member COUNTRY
 * CODES (not the region code); these helpers collapse a fully-selected region back to its
 * label for display, while a lone country stays its own name.
 */
import {
  ALL_TERRITORIES,
  REGION_LABELS,
  TERRITORY_ZONES,
  type TerritoryRegion,
} from "@/data/territories";

export const REGION_ORDER: TerritoryRegion[] = [
  "EU",
  "UK",
  "NA",
  "LATAM",
  "SA",
  "AFRICA",
  "MEENA",
  "APAC",
];

const LEGACY_REGION_ALIASES: Record<string, TerritoryRegion> = {
  MENA: "MEENA",
};

/**
 * The ONE form a region may take, anywhere: `r:EU`, `r:NA`. Owner decision 2026-09-21.
 *
 * A bare two-letter region key cannot be told apart from a country: `NA` is both North America
 * and NAMIBIA, `SA` is both South America and SAUDI ARABIA — and both countries are listed in
 * this very file (Namibia inside AFRICA, Saudi Arabia inside MEENA). Until now `resolveRegion`
 * matched bare keys first, so a stored list of African countries came back through
 * `normalizeSelection` with Namibia replaced by Bermuda, Canada, Greenland, St Pierre & Miquelon
 * and the United States — a grant across an ocean nobody made. It bit on EDIT, not on read: the
 * value only changed when a human opened the picker and saved.
 *
 * The backend never had the bug (`territory_service._region_key_from_token` refuses a bare key
 * that is a real country code), so the two sides disagreed about the same stored bytes. The
 * prefix is the backend's existing canonical form (`REGION_PREFIX = "r:"`), and the territory
 * CHECK constraint already accepts it.
 *
 * Live data at the time of the change: 8 bare region keys, all on test labels, migrated to the
 * prefixed form; 0 on FELT or BELIEVE. Every `NA` / `SA` in real data (4,551 occurrences across
 * 2,306 BELIEVE rows) is a COUNTRY and now stays one.
 */
const REGION_PREFIX = "r:";

export const REGION_OPTIONS = REGION_ORDER.map((region) => ({
  value: region,
  label: REGION_LABELS[region],
  countries: TERRITORY_ZONES[region].map((territory) => territory.code),
}));

const COUNTRY_CODE_ORDER = new Map(
  ALL_TERRITORIES.map((territory, index) => [territory.code, index])
);

const COUNTRY_CODE_SET = new Set(ALL_TERRITORIES.map((territory) => territory.code));

const COUNTRY_NAMES = new Set(ALL_TERRITORIES.map((territory) => territory.name));

/**
 * Regions named like a country. The "United Kingdom" region is GB + Guernsey, Jersey and the Isle
 * of Man — the UK as a licensing territory — and folded into a chip with the name of the country GB.
 * Derived, not listed: a region relabelled into a country's name joins this set on its own.
 */
export const COUNTRY_NAMED_REGIONS: ReadonlySet<TerritoryRegion> = new Set(
  REGION_OPTIONS.filter((region) => COUNTRY_NAMES.has(region.label)).map((region) => region.value)
);

function resolveRegion(value: string): TerritoryRegion | null {
  const raw = value.trim();
  // Prefixed form ONLY. A bare "NA" is Namibia, never North America — see REGION_PREFIX above.
  if (!raw.toLowerCase().startsWith(REGION_PREFIX)) return null;
  const normalized = raw.slice(REGION_PREFIX.length).trim().toUpperCase();
  if (normalized in TERRITORY_ZONES) return normalized as TerritoryRegion;
  return LEGACY_REGION_ALIASES[normalized] || null;
}

/**
 * Expand any region codes to their member country codes, dedup, and sort into canonical
 * order. `"worldwide"` short-circuits to `["worldwide"]`.
 */
export function normalizeSelection(values: string[]): string[] {
  if (values.some((value) => value.toLowerCase() === "worldwide")) return ["worldwide"];

  const codes = new Set<string>();
  values.forEach((value) => {
    const region = resolveRegion(value);
    if (region) {
      TERRITORY_ZONES[region].forEach((territory) => codes.add(territory.code));
      return;
    }

    const code = value.toUpperCase();
    if (COUNTRY_CODE_SET.has(code)) codes.add(code);
  });

  return [...codes].sort(
    (a, b) => (COUNTRY_CODE_ORDER.get(a) ?? 9999) - (COUNTRY_CODE_ORDER.get(b) ?? 9999)
  );
}

/** Display label for a single territory/region/worldwide code. */
export function getTerritoryLabel(value: string): string {
  if (value.toLowerCase() === "worldwide") return "Worldwide";
  const region = resolveRegion(value);
  if (region) return REGION_LABELS[region];
  const territory = ALL_TERRITORIES.find((t) => t.code === value);
  if (territory) return territory.name;
  return value;
}

export type DisplaySelection =
  | { type: "worldwide"; value: "worldwide"; label: string }
  | { type: "region"; value: TerritoryRegion; label: string; countries: string[] }
  | { type: "country"; value: string; label: string };

/**
 * Collapse a selection into worldwide / fully-selected regions / leftover countries —
 * the shape the picker renders as removable chips and the display helpers map to labels.
 *
 * `forEditing` (the picker's own tags): a region named like a country (`COUNTRY_NAMED_REGIONS`)
 * is NOT folded — its countries are listed, each a tag that can be seen and removed. Folded, "UK &
 * Ireland" showed as "United Kingdom, Ireland" and Guernsey, Jersey and the Isle of Man were stored
 * yet invisible, going with "United Kingdom" when it was removed (2026-10-25). A reader keeps the
 * fold: there "United Kingdom" is the territory's name.
 */
export function getDisplaySelections(
  values: string[],
  { forEditing = false }: { forEditing?: boolean } = {}
): DisplaySelection[] {
  const normalized = normalizeSelection(values);
  if (normalized.includes("worldwide")) {
    return [{ type: "worldwide", value: "worldwide", label: "Worldwide" }];
  }

  const remaining = new Set(normalized);
  const selections: DisplaySelection[] = [];

  REGION_OPTIONS.forEach((region) => {
    if (forEditing && COUNTRY_NAMED_REGIONS.has(region.value)) return;
    const allSelected = region.countries.every((code) => remaining.has(code));
    if (!allSelected) return;

    selections.push({
      type: "region",
      value: region.value,
      label: region.label,
      countries: region.countries,
    });
    region.countries.forEach((code) => remaining.delete(code));
  });

  ALL_TERRITORIES.forEach((territory) => {
    if (!remaining.has(territory.code)) return;
    selections.push({
      type: "country",
      value: territory.code,
      label: territory.name,
    });
  });

  return selections;
}

/**
 * Region-collapsed display labels for a selection, in order
 * (Worldwide / fully-selected regions → region label / leftover countries → name).
 */
export function getTerritoryLabels(values: string[]): string[] {
  return getDisplaySelections(values).map((selection) => selection.label);
}

/** Region-collapsed, comma-joined summary (e.g. `"Europe, United States"`). */
export function getTerritorySummary(values: string[]): string {
  return getTerritoryLabels(values).join(", ");
}
